import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { toast } from 'react-toastify';
import { DocumentTextIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { getDraftRecoverySnapshot, subscribeDraftRecovery } from '../../utils/draftRecoveryPrompts';
import { formatClock, formatDateOnly, formatSavedAt } from '../../utils/draftTimeFormat';

// Date fields a clinical window may use, in the order they are most common.
const DATE_FIELD_SELECTOR = [
  'sessionDate', 'assessmentDate', 'planDate', 'contactDate', 'consultDate', 'startDate', 'documentDate',
].map((name) => `input[name="${name}"]`).join(', ');

// `!` because the global dark stylesheet borders every button and turns it gray
// on hover with !important (index.css, darkModeEnhanced.css).
const BUTTON = 'rounded-md px-3.5 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50';
const PRIMARY = `${BUTTON} !border-0 !bg-blue-700 font-semibold !text-white hover:!bg-blue-800 dark:!bg-blue-600 dark:hover:!bg-blue-500`;
const SECONDARY = `${BUTTON} !border !border-solid !border-gray-300 !bg-white font-medium !text-gray-800 hover:!bg-gray-50 dark:!border-gray-600 dark:!bg-gray-700 dark:!text-gray-100 dark:hover:!bg-gray-600`;
const QUIET = `${BUTTON} !border-0 !bg-transparent !text-gray-600 hover:!text-gray-900 dark:!text-gray-300 dark:hover:!bg-transparent dark:hover:!text-white`;
const TITLE_ID = 'draft-recovery-title';

const promptKey = (prompt) => (prompt ? `${prompt.id}|${prompt.kind}|${prompt.savedAt || ''}` : null);

// What each question says. `bar` is the reminder left after "Decide later";
// a question without one must be answered.
function describe(prompt) {
  const who = prompt.patientName || 'this client';
  const onDate = prompt.sessionDate ? ` on ${formatDateOnly(prompt.sessionDate)}` : '';
  const savedAt = formatSavedAt(prompt.savedAt);
  if (prompt.kind === 'conflict') {
    return {
      tone: 'amber',
      title: 'This note is not saved',
      body: `A draft for ${who}${onDate} was saved from another device or window${savedAt ? ` ${savedAt}` : ''}. Load that draft to continue from it, or keep what you typed here and replace it.`,
      note: 'Nothing is erased either way: a field left blank in the copy you choose is filled from the other, and the replaced copy stays in the draft history. Autosave is paused until you choose.',
      bar: `Another copy of this draft was saved${savedAt ? ` ${savedAt}` : ''}. Autosave is paused until you choose which copy to keep.`,
    };
  }
  if (prompt.kind === 'gone') {
    return {
      tone: 'amber',
      title: 'This draft no longer exists',
      body: `The draft for ${who}${onDate} was completed or deleted, possibly in another window. What you typed here has not been saved. Save it as a new draft to keep it.`,
      note: 'If this note was already completed, check the client’s documents before saving, so it is not written twice. Your text stays on this device until you choose.',
      bar: 'This draft was completed or deleted. Save your text as a new draft to keep it.',
    };
  }
  const details = prompt.details || {};
  const start = formatClock(details.sessionStartTime);
  const end = formatClock(details.sessionEndTime);
  const facts = [savedAt && `Last saved ${savedAt}`, start && end && `Session ${start}–${end}`, details.clientAttested && 'Client attestation captured on mobile']
    .filter(Boolean).join(' · ');
  return {
    tone: 'blue',
    title: `You already have a draft for ${who}${onDate}`,
    body: `${facts ? `${facts}. ` : ''}Open it to keep working, so nothing is written twice. Nothing on this form has been saved yet.`,
    note: null,
    bar: null,
  };
}

function DeferredBar({ text, onChoose }) {
  return (
    <div
      role="alert"
      className="fixed left-1/2 top-2 z-[9000] flex w-[min(900px,calc(100vw-32px))] -translate-x-1/2 items-center justify-between gap-4 rounded-lg border border-solid border-amber-300 bg-amber-50 px-4 py-2.5 text-amber-900 shadow-lg dark:border-amber-700 dark:bg-amber-900 dark:text-amber-100"
    >
      <div className="flex items-center gap-2.5 text-sm">
        <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-300" aria-hidden="true" />
        <span><strong>Not saved.</strong> {text}</span>
      </div>
      <button
        type="button"
        onClick={onChoose}
        className="flex-shrink-0 rounded-md !border !border-solid !border-amber-500 !bg-white px-3.5 py-1.5 text-sm font-semibold !text-amber-800 hover:!bg-amber-100 dark:!bg-amber-950 dark:!text-amber-200 dark:hover:!bg-amber-800"
      >
        Choose
      </button>
    </div>
  );
}

/**
 * Asks the question a clinical window is stuck on: which copy of a draft to
 * keep, or where to save text whose draft was removed. Mounted once at the app
 * root; any window saving through useDraftManager publishes its question to
 * utils/draftRecoveryPrompts.
 */
export default function DraftRecoveryPrompt() {
  const prompt = useSyncExternalStore(subscribeDraftRecovery, getDraftRecoverySnapshot);
  const [deferredKey, setDeferredKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const primaryRef = useRef(null);
  const key = promptKey(prompt);
  const deferred = Boolean(key) && deferredKey === key;

  useEffect(() => {
    if (prompt && !deferred) primaryRef.current?.focus();
  }, [key, deferred]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!prompt) return null;

  const run = async (name, failureMessage) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const outcome = await prompt.actions.current?.[name]?.();
      // A new conflict re-opens the prompt by itself; anything else is a failure.
      const failed = outcome === false || (outcome?.success === false && !outcome?.conflict);
      if (failed) toast.error(failureMessage, { toastId: `draft-recovery-${name}` });
    } catch (error) {
      console.error('Draft recovery choice failed:', error?.message);
      toast.error('That did not work. Your edits are still on this screen; try again.', { toastId: 'draft-recovery-error' });
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const text = describe(prompt);
  if (deferred && text.bar) return <DeferredBar text={text.bar} onChoose={() => setDeferredKey(null)} />;

  const decideLater = <button type="button" className={QUIET} disabled={busy} onClick={() => setDeferredKey(key)}>Decide later</button>;
  const actions = {
    conflict: (
      <>
        {decideLater}
        <button type="button" className={SECONDARY} disabled={busy}
          onClick={() => run('keepLocalEdits', 'Your edits were not saved to the server. They are still on this screen and this device; try again.')}>Keep my edits</button>
        <button type="button" ref={primaryRef} className={PRIMARY} disabled={busy}
          onClick={() => run('adoptServerDraft', 'The other draft could not be loaded. Your edits are still on this screen; try again.')}>Load the other draft</button>
      </>
    ),
    gone: (
      <>
        {decideLater}
        <button type="button" ref={primaryRef} className={PRIMARY} disabled={busy}
          onClick={() => run('saveAsNewDraft', 'Your text was not saved as a new draft. It is still on this screen and this device; try again.')}>Save as a new draft</button>
      </>
    ),
    existing: (
      <>
        <button type="button" className={SECONDARY} disabled={busy} onClick={() => {
          prompt.actions.current?.dismissExistingDraft?.();
          document.querySelector(DATE_FIELD_SELECTOR)?.focus();
        }}>Choose a different date</button>
        <button type="button" ref={primaryRef} className={PRIMARY} disabled={busy}
          onClick={() => run('openExistingDraft', 'That draft could not be opened. Try again, or choose a different date.')}>Open that draft</button>
      </>
    ),
  };
  const amber = text.tone === 'amber';

  return (
    <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-gray-900/45 p-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        className="flex w-full max-w-[500px] flex-col gap-3.5 overflow-hidden rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800"
      >
        {/* Accent strip, not a border: the app runs without Tailwind's base reset,
            so a top-border class alone draws nothing. */}
        <div aria-hidden="true" className={`-mx-6 -mt-6 h-1 ${amber ? 'bg-amber-500' : 'bg-blue-600'}`} />
        <div className="flex items-start gap-3">
          {amber
            ? <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
            : <DocumentTextIcon className="h-6 w-6 flex-shrink-0 text-blue-600 dark:text-blue-400" aria-hidden="true" />}
          <div className="flex flex-col gap-1.5">
            <h2 id={TITLE_ID} className="m-0 text-base font-semibold text-gray-900 dark:text-white">{text.title}</h2>
            <p className="m-0 text-sm leading-relaxed text-gray-700 dark:text-gray-300">{text.body}</p>
          </div>
        </div>
        {text.note && <p className="m-0 ml-9 text-xs leading-relaxed text-gray-600 dark:text-gray-400">{text.note}</p>}
        <div className="flex flex-wrap items-center justify-end gap-2 pt-1">{actions[prompt.kind] || actions.existing}</div>
      </div>
    </div>
  );
}
